import React, { useEffect } from 'react';
import { Assets, Colors, Main, Models, TabsSwitch } from 'utils/imports.utils';
import './account_settings.screen.scss';
import { AccountSettingsTabs } from 'utils/interface.utils';
import user from 'models/user.model';
import { getStoredRole, mergeInvoicePages, toastifyError, toastifySuccess, useSetState } from 'utils/functions.utils';
import { INVOICE_PAGE_SIZE } from 'utils/constant.utils';
import { STRINGS } from 'constants/strings.constant';
import { GetUserInvoicesResponse, Invoice } from 'interfaces/invoice.interface';
import { useLocation, useNavigate } from 'react-router-dom';
import { ListSupportTicketsResponse, SupportTicketRow } from 'interfaces/support.interface';
import AccountProfileTab from 'components/account_profile_tab/account_profile_tab.component';
import AccountPasswordTab from 'components/account_password_tab/account_password_tab.component';
import AccountBillsTab from 'components/account_bills_tab/account_bills_tab.component';
import AccountSupportTab from 'components/account_support_tab/account_support_tab.component';
import UnsavedChangesPopup from 'components/unsaved_changes_popup/unsaved_changes_popup.component';

const AccountSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useSetState({
    fullName: '',
    emailId: '',
    industry: { label: 'IT', value: 'IT' },
    type: { label: 'Design', value: 'Design' },
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    profileData: null, //details of login user
    savedProfile: null, // name, industry, type and photo as the server last had them (what Cancel compares to)
    showUnsavedWarning: false,
    profilePhotoSrc: null,
    profilePicture: null,
    invoices: [] as Invoice[],
    invoicesTotal: 0,
    invoicesLoading: true,
    invoicesLoadingMore: false,
    invoicesError: false,
    isDisabledPassword: true,
    activityPassword: false,
    activitySave: false,
    supportSubject: '',
    supportDescription: '',
    supportAttachment: null,
    supportSubmitting: false,
    supportView: 'list',
    supportTickets: [] as SupportTicketRow[],
    supportTicketsLoading: true,
  });
  useEffect(() => {
    const disabledPassword =
      !state.currentPassword.trim() ||
      !state.newPassword.trim() ||
      !state.confirmPassword.trim() ||
      state.activityPassword;

    setState({ isDisabledPassword: disabledPassword });
  }, [state.currentPassword, state.newPassword, state.confirmPassword]);

  const handleChangePassword = async () => {
    setState({ activityPassword: true });
    if (
      !state.currentPassword ||
      !state.newPassword ||
      !state.confirmPassword
    ) {
      console.log('All fields are required');
      toastifyError('All fields are required');
      return;
    }

    if (state.newPassword !== state.confirmPassword) {
      console.log('New password and confirm password do not match');
      toastifyError('New password and confirm password do not match');
      return;
    }

    setState({ loading: true });

    try {
      await Models.auth.changePassword({
        old_password: state.currentPassword,
        password: state.newPassword,
        confirm_password: state.confirmPassword,
      });
      console.log('Password changed successfully');
      toastifySuccess('Password changed successfully');

      setState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        loading: false,
      });
    } catch (error: any) {
      toastifyError(error.response?.data?.message || "Failed to change password");
      setState({ loading: false });
    } finally {
      setState({ activityPassword: false });
    }
  };

  const fetchUserDetails = async () => {
    try {
      const response: any = await Models.auth.getUserProfile();
      console.log('profile data', response.data);
      const industry = response.data.industry
        ? { label: response.data.industry, value: response.data.industry }
        : state.industry;
      const type = response.data.industry_type
        ? { label: response.data.industry_type, value: response.data.industry_type }
        : state.type;
      const photo = response.data.profile_picture || Assets.user;
      setState({
        profileData: response.data,
        industry,
        type,
        profilePhotoSrc: photo,
        savedProfile: {
          username: response.data.username,
          industry: industry.value,
          type: type.value,
          photo,
        },
      });
    } catch (err) {
      console.log('Failed to fetch profile Data', err);
      toastifyError('Failed to load profile details');
    }
  };

  // Loads one page of the user's payment invoices. The first page replaces what is shown; later pages
  // are appended. A failure keeps whatever is already on screen and shows an error line instead.
  const getInvoices = async (skip: number) => {
    const isFirstPage = skip === 0;
    setState(isFirstPage ? { invoicesLoading: true, invoicesError: false } : { invoicesLoadingMore: true, invoicesError: false });
    try {
      const response: GetUserInvoicesResponse = await Models.auth.getUserInvoices({ skip, limit: INVOICE_PAGE_SIZE });
      const docs = response.data?.docs ?? [];
      setState({
        invoices: isFirstPage ? docs : mergeInvoicePages(state.invoices, docs),
        invoicesTotal: response.data?.totalDocs ?? 0,
      });
    } catch (err) {
      console.error('Failed to fetch invoices', err);
      setState({ invoicesError: true });
    } finally {
      setState({ invoicesLoading: false, invoicesLoadingMore: false });
    }
  };

  const handleSave = async (): Promise<boolean> => {
    setState({ activitySave: true });
    try {
      let uploadedImageUrl = '';
      if (state.profilePicture) {
        uploadedImageUrl = state.profilePhotoSrc;
      }
      const jsonData = {
        username: state.profileData.username,
        // email is intentionally omitted — edit_user has never supported changing it (the
        // backend strips it from the request body outright). See docs/BUGFIXES.md.
        ...(!isAdmin && { industry: state.industry?.value, industry_type: state.type?.value }),
        ...(uploadedImageUrl && { profile_picture: uploadedImageUrl }),
      };
      const response: any = await Models.auth.editUser(jsonData);
      if (response) {
        await fetchUserDetails();
        setState({ profileData: response.data });
        toastifySuccess('User updated successful');
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('User update failed:', error);
      toastifyError('User update failed');
      return false;
    } finally {
      setState({ activitySave: false });
    }
  };

  const hasUnsavedChanges = (): boolean => {
    const saved = state.savedProfile;
    if (!saved) return false;
    return (
      state.profileData?.username !== saved.username ||
      state.industry?.value !== saved.industry ||
      state.type?.value !== saved.type ||
      state.profilePhotoSrc !== saved.photo
    );
  };

  // A page opened directly has nothing behind it, so going back would do nothing.
  const goBack = () => {
    if (location.key === 'default') {
      navigate('/welcome');
    } else {
      navigate(-1);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges()) {
      setState({ showUnsavedWarning: true });
    } else {
      goBack();
    }
  };

  const handleDiscardChanges = () => {
    const saved = state.savedProfile;
    if (saved) {
      setState({
        profileData: { ...state.profileData, username: saved.username },
        industry: { label: saved.industry, value: saved.industry },
        type: { label: saved.type, value: saved.type },
        profilePhotoSrc: saved.photo,
        profilePicture: null,
      });
    }
    setState({ showUnsavedWarning: false });
    goBack();
  };

  const handleSaveFromWarning = async () => {
    const saved = await handleSave();
    if (saved) setState({ showUnsavedWarning: false });
  };

  const handlePhotoUpload = async (file: File) => {
    console.log('Selected file:', file);
    const fileType = file.type.split('/')[1];

    if (!['jpeg', 'png'].includes(fileType)) {
      console.error('Unsupported file type:', fileType);
      toastifyError('Unsupported file type. Please upload a JPEG or PNG image.');
      return;
    }

    try {
      const uploadResult: any = await user.upload(file);

      if (uploadResult?.fileUrl) {
        setState({
          profilePicture: file,
          profilePhotoSrc: uploadResult.fileUrl,
        });
      } else {
        throw new Error('Image upload failed');
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      toastifyError('Image upload failed');
    }
  };

  const fetchSupportTickets = async () => {
    setState({ supportTicketsLoading: true });
    try {
      const response: ListSupportTicketsResponse = await Models.auth.listSupportTickets({ skip: 0, limit: 20 });
      setState({ supportTickets: response?.data?.docs || [] });
    } catch (err) {
      console.error('Failed to fetch support tickets', err);
    } finally {
      setState({ supportTicketsLoading: false });
    }
  };

  const handleCreateSupportTicket = async () => {
    if (!state.supportSubject.trim() || !state.supportDescription.trim()) {
      toastifyError('Subject and description are required');
      return;
    }
    setState({ supportSubmitting: true });
    try {
      const formData = new FormData();
      formData.append('subject', state.supportSubject.trim());
      formData.append('description', state.supportDescription.trim());
      if (state.supportAttachment) {
        formData.append('images', state.supportAttachment);
      }
      await Models.auth.createSupportTicket(formData);
      toastifySuccess('Ticket submitted');
      setState({ supportSubject: '', supportDescription: '', supportAttachment: null, supportView: 'list' });
      fetchSupportTickets();
    } catch (error) {
      console.error('Failed to create support ticket', error);
      toastifyError('Failed to submit ticket');
    } finally {
      setState({ supportSubmitting: false });
    }
  };

  const handleSizeError = (file: File) => {
    console.error(`File size exceeds limit: ${file.name}`);
  };

  const handleTypeError = (error: string) => {
    console.error(`File type error: ${error}`);
  };

  const handleSupportAttachmentUpload = (file: File) => {
    setState({ supportAttachment: file });
  };

  const handleSupportAttachmentSizeError = () => {
    toastifyError('Attachment must be 10MB or smaller');
  };

  const handleSupportAttachmentTypeError = () => {
    toastifyError('Only image attachments are allowed');
  };

  useEffect(() => {
    let token = localStorage.getItem('token');
    if (token) {
      fetchUserDetails();
      getInvoices(0);
      fetchSupportTickets();
      // handleSave()
    } else {
      navigate('/login');
    }
  }, []);

  // An admin has no plan, invoices, support tickets, industry or type of work: those are user-only.
  const isAdmin = (state.profileData?.role ?? getStoredRole()) === 'admin';

  const tabPanels = [
    {
      label: AccountSettingsTabs.PROFILE,
      component: () => (
        <AccountProfileTab
          profileData={state.profileData}
          photoSrc={state.profilePhotoSrc}
          defaultPhotoSrc={Assets.user}
          industry={state.industry}
          type={state.type}
          showWorkFields={!isAdmin}
          isSaving={state.activitySave}
          onNameChange={(username) => setState({ profileData: { ...state.profileData, username } })}
          onIndustryChange={(industry) => setState({ industry })}
          onTypeChange={(type) => setState({ type })}
          onPhotoUpload={handlePhotoUpload}
          onPhotoSizeError={handleSizeError}
          onPhotoTypeError={handleTypeError}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      ),
    },
    {
      label: AccountSettingsTabs.CHANGE_PASSWORD,
      component: () => (
        <AccountPasswordTab
          currentPassword={state.currentPassword}
          newPassword={state.newPassword}
          confirmPassword={state.confirmPassword}
          isDisabled={state.isDisabledPassword}
          isSubmitting={state.activityPassword}
          onCurrentPasswordChange={(currentPassword) => setState({ currentPassword })}
          onNewPasswordChange={(newPassword) => setState({ newPassword })}
          onConfirmPasswordChange={(confirmPassword) => setState({ confirmPassword })}
          onSubmit={handleChangePassword}
        />
      ),
    },
    ...(isAdmin ? [] : [{
      label: AccountSettingsTabs.MY_BILLS,
      component: () => (
        <AccountBillsTab
          invoices={state.invoices}
          total={state.invoicesTotal}
          isLoading={state.invoicesLoading}
          isLoadingMore={state.invoicesLoadingMore}
          hasError={state.invoicesError}
          onLoadMore={() => getInvoices(state.invoices.length)}
        />
      ),
    }]),
    ...(isAdmin ? [] : [{
      label: AccountSettingsTabs.SUPPORT,
      component: () => (
        <AccountSupportTab
          view={state.supportView}
          tickets={state.supportTickets}
          isLoading={state.supportTicketsLoading}
          subject={state.supportSubject}
          description={state.supportDescription}
          attachment={state.supportAttachment}
          isSubmitting={state.supportSubmitting}
          onNewTicket={() => setState({ supportView: 'new' })}
          onBack={() => setState({ supportView: 'list' })}
          onSubjectChange={(supportSubject) => setState({ supportSubject })}
          onDescriptionChange={(supportDescription) => setState({ supportDescription })}
          onAttachmentUpload={handleSupportAttachmentUpload}
          onAttachmentSizeError={handleSupportAttachmentSizeError}
          onAttachmentTypeError={handleSupportAttachmentTypeError}
          onSubmit={handleCreateSupportTicket}
        />
      ),
    }]),
  ];

  return (
    <Main isRenderOnlyLogo={false} backgroundColor={Colors.screenBackground}>
      <div className="account_settings">
        <div className="account_settings_container">
          <div className="account_settings_header">
            <button
              type="button"
              className="account_settings_header_back"
              onClick={() => navigate(isAdmin ? '/admin' : '/welcome')}>
              <img src={Assets.arrow_left} alt="" />
              {isAdmin ? STRINGS.accountSettings.backToOverview : STRINGS.accountSettings.backToProjects}
            </button>
            <h1 className="account_settings_header_title">{STRINGS.accountSettings.title}</h1>
          </div>
          <div className="account_settings_tabs_switch_container">
            <TabsSwitch
              tabsStyle={{}}
              tabsListStyle={{}}
              tabsItemStyle={{}}
              tabsItems={tabPanels.map((panel) => panel.label)}
              tabsPanels={tabPanels}
            />
          </div>
        </div>
      </div>
      <UnsavedChangesPopup
        isOpen={state.showUnsavedWarning}
        isSaving={state.activitySave}
        onDiscard={handleDiscardChanges}
        onSave={handleSaveFromWarning}
        onClose={() => setState({ showUnsavedWarning: false })}
      />
    </Main>
  );
};

export default AccountSettings;
